// Client-side error reporting: Grafana Faro, started below. Runs whenever a
// user loads a page in their browser.

import { isChunkErrorAwaitingReload } from '@/lib/chunk-reload';
import {
  areAllFramesFromCcm19,
  areAllFramesFromTikTokPixel,
  areAllFramesMasked,
  isAutomatedBrowser,
  isIgnoredErrorMessage,
  isIgnoredLogMessage,
  isInAppInjectedError,
  isProtectedHostServiceWorkerError,
  isRecaptchaTeardownError
} from '@/lib/ignored-errors';
import { scrubPii } from '@/lib/scrub-pii';

// Faro ships errors and the console lines around them, nothing else — see
// the instrumentations filter below.
const ERROR_INSTRUMENTATIONS: readonly string[] = [
  '@grafana/faro-web-sdk:instrumentation-errors',
  '@grafana/faro-web-sdk:instrumentation-console'
];

// Self-hosted client error/log tracking (no vendor quota). Runs only when
// NEXT_PUBLIC_FARO_URL is set; SDK loads dynamically.
async function initFaroIfConfigured() {
  const faroUrl = process.env.NEXT_PUBLIC_FARO_URL;
  const appEnv = process.env.NEXT_PUBLIC_APP_ENV;
  if (!faroUrl || !appEnv) {
    if (faroUrl) {
      // eslint-disable-next-line no-console -- a misconfigured build still needs a visible diagnostic in the browser
      console.warn(
        'Faro is enabled but NEXT_PUBLIC_APP_ENV is unset; skipping so production errors are not mislabeled as development.'
      );
    }
    return;
  }

  try {
    const {
      getWebInstrumentations,
      initializeFaro,
      LogLevel,
      TransportItemType
    } = await import('@grafana/faro-web-sdk');

    initializeFaro({
      url: faroUrl,
      app: {
        name: 'ihrhelden-web',
        environment: appEnv,
        // Which build threw it — set from package.json in next.config.mjs.
        version: process.env.NEXT_PUBLIC_APP_VERSION
      },
      // Issues only. Everything Faro can report about what a person did —
      // clicks (user-action), page views (view/navigation), a session id in
      // sessionStorage (session), timings (web-vitals/performance) — is left
      // out; what is left is the throw and the console line next to it. Faro
      // writes nothing to the visitor's device, so this needs no
      // cookie-consent entry and runs for everyone.
      instrumentations: getWebInstrumentations().filter((instrumentation) =>
        ERROR_INSTRUMENTATIONS.includes(instrumentation.name)
      ),
      sessionTracking: { enabled: false },
      // console.log/debug/trace aren't captured — args can carry user PII (e.g. form emails).
      consoleInstrumentation: {
        disabledLevels: [LogLevel.TRACE, LogLevel.DEBUG, LogLevel.LOG]
      },
      beforeSend(item) {
        // meta is shared across items, so replace rather than mutate it.
        const page = item.meta?.page;
        if (page?.url) {
          item.meta = {
            ...item.meta,
            page: { ...page, url: scrubPii(page.url) }
          };
        }
        if (item.type === TransportItemType.EXCEPTION) {
          if (isAutomatedBrowser()) return null;
          const payload = item.payload as {
            type?: string;
            value?: string;
            stacktrace?: { frames?: { filename?: string }[] };
          };
          // The list PostHog also filters on — third-party noise should be
          // missing from both reporters or present in both, and Grafana is the
          // one dashboard the team actually reads.
          // Returning a falsy item drops it (`applyBeforeSendHooks`).
          if (
            isIgnoredErrorMessage(payload.value) ||
            isRecaptchaTeardownError(payload.value) ||
            isProtectedHostServiceWorkerError(payload.value, appEnv) ||
            isChunkErrorAwaitingReload({
              name: payload.type,
              message: payload.value
            }) ||
            areAllFramesMasked(payload.stacktrace?.frames) ||
            areAllFramesFromCcm19(payload.stacktrace?.frames) ||
            areAllFramesFromTikTokPixel(payload.stacktrace?.frames) ||
            isInAppInjectedError({
              userAgent: navigator.userAgent,
              pageUrl: window.location.href,
              frames: payload.stacktrace?.frames
            })
          ) {
            return null;
          }
          if (payload.value) payload.value = scrubPii(payload.value);
          // Frame filenames are the page URL again — same query string, same
          // click IDs — so they need the same treatment as the message.
          for (const frame of payload.stacktrace?.frames ?? []) {
            if (frame.filename) frame.filename = scrubPii(frame.filename);
          }
        }
        if (item.type === TransportItemType.LOG) {
          const payload = item.payload as { message?: string };
          // Console instrumentation also reports third-party `console.error`
          // lines (e.g. the autofill extension). Only the extension-specific log
          // list applies here, never the broad exception list, so our own
          // console.error lines are not dropped.
          if (isIgnoredLogMessage(payload.message)) return null;
          if (payload.message) payload.message = scrubPii(payload.message);
        }
        return item;
      }
    });
  } catch (error) {
    // Reporting must fail open: a broken Faro init must never take the page down.
    // eslint-disable-next-line no-console -- fail-open diagnostics must not rely on Faro itself
    console.error(
      'Faro initialization failed; client tracking disabled.',
      error
    );
  }
}

void initFaroIfConfigured();
